import DataTable, { type TableColumn, type SortOrder } from "react-data-table-component";
import { useMemo, useCallback } from "react";
import { Typography } from "../atoms/Typography";
import { dataTableCustomStyles } from "../../config/dataTableTheme";
import type { LegalEntity } from "../../types/legalEntity";
import type { LegalEntitySortBy } from "../../services/legalEntityService";
import { createDataTablePagination } from "./DataTablePagination";

const legalEntitiesTablePagination = createDataTablePagination({
  rowsPerPageOptions: [10, 25, 50],
});

type LegalEntitiesTableProps = {
  readonly entities: LegalEntity[];
  readonly onToggleStatus: (id: string, newStatus: "Active" | "Inactive") => void;
  readonly totalRows: number;
  readonly isLoading: boolean;
  readonly page: number;
  readonly perPage: number;
  readonly onChangePage: (page: number) => void;
  readonly onChangeRowsPerPage: (perPage: number, page: number) => void;
  readonly sortBy?: LegalEntitySortBy;
  readonly sortDir?: "asc" | "desc";
  readonly onSort?: (sortBy: LegalEntitySortBy, sortDir: "asc" | "desc") => void;
};

const StatusToggle = ({
  entity,
  onToggleStatus,
}: {
  readonly entity: LegalEntity;
  readonly onToggleStatus: (id: string, newStatus: "Active" | "Inactive") => void;
}) => {
  const isActive = entity.status === "Active";
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={isActive}
        onClick={() => onToggleStatus(entity.id, isActive ? "Inactive" : "Active")}
        className={
          isActive
            ? "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-primary-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
            : "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
        }
      >
        <span
          className={
            isActive
              ? "pointer-events-none inline-block h-5 w-5 translate-x-5 rounded-full bg-white shadow-sm ring-0 transition-transform"
              : "pointer-events-none inline-block h-5 w-5 translate-x-0 rounded-full bg-white shadow-sm ring-0 transition-transform"
          }
        />
      </button>
      <span className="text-sm text-gray-700">{entity.status}</span>
    </div>
  );
};
export const LegalEntitiesTable = ({
  entities,
  onToggleStatus,
  totalRows,
  isLoading,
  page,
  perPage,
  onChangePage,
  onChangeRowsPerPage,
  sortBy = "created_at",
  sortDir = "desc",
  onSort,
}: LegalEntitiesTableProps) => {
  const handleSort = useCallback(
    (selectedColumn: TableColumn<LegalEntity>, sortDirection: SortOrder) => {
      const id = selectedColumn.id == null ? "created_at" : String(selectedColumn.id);
      const apiSortBy = id as LegalEntitySortBy;
      onSort?.(apiSortBy, sortDirection === "asc" ? "asc" : "desc");
    },
    [onSort],
  );

  const columns: TableColumn<LegalEntity>[] = useMemo(
    () => [
      {
        id: "entity_name",
        name: "Entity Name",
        selector: (row) => row.entityName,
        sortable: true,
        grow: 2,
        cell: (row) => <Typography variant="body">{row.entityName}</Typography>,
      },
      // {
      //   id: "name",
      //   name: "Name",
      //   selector: (row) => row.name,
      //   sortable: true,
      //   cell: (row) => <Typography variant="body">{row.name}</Typography>
      // },
      // {
      //   id: "title",
      //   name: "Title",
      //   selector: (row) => row.title,
      //   sortable: true,
      //   cell: (row) => <Typography variant="body">{row.title}</Typography>
      // },
      {
        name: "Address",
        selector: (row) => row.address,
        sortable: false,
        grow: 2,
        cell: (row) => (
          <Typography variant="body" className="text-gray-600">
            {row.address}
          </Typography>
        ),
      },
      {
        id: "created_by",
        name: "Created By",
        selector: (row) => row.createdBy,
        sortable: true,
      },
      {
        id: "created_at",
        name: "Created On",
        selector: (row) => row.createdOn,
        sortable: true,
      },
      {
        id: "updated_by",
        name: "Modified By",
        selector: (row) => row.modifiedBy,
        sortable: true,
      },
      {
        name: "Status",
        cell: (row) => <StatusToggle entity={row} onToggleStatus={onToggleStatus} />,
        width: "180px",
      },
    ],
    [onToggleStatus],
  );

  return (
    <DataTable
      key={`${sortBy}-${sortDir}`}
      columns={columns}
      data={entities}
      customStyles={dataTableCustomStyles}
      striped={false}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      paginationDefaultPage={page}
      paginationPerPage={perPage}
      paginationRowsPerPageOptions={[10, 25, 50]}
      paginationComponent={legalEntitiesTablePagination}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      progressPending={isLoading}
      noDataComponent={
        <div className="p-12 text-center">
          <Typography variant="muted">
            No legal entities found. Add your first legal entity to get started.
          </Typography>
        </div>
      }
      highlightOnHover
      responsive
      sortServer
      onSort={onSort ? handleSort : undefined}
      defaultSortFieldId={sortBy}
      defaultSortAsc={sortDir === "asc"}
    />
  );
};
